import { useEffect, useState } from "react";
import { FaRegTrashAlt } from "react-icons/fa";
import { TillageTask } from "../../../interfaces/TillageTask";
import ParcelService from "../../../services/parcelService";
import { FiPlus } from 'react-icons/fi';
import { FormModal } from "../../layout/Modal/Modal";
import TillageCreateForm from "./TillageCreateForm";
import IconButton from "../../common/IconButton";
import { formatDateDisplay } from "../../../utils/date";


interface FormProps {
    parcelId: number
    cycleId: number
    cycleCompleted?: boolean
}

const TillageForm = (props: FormProps) => {
    const parcelService = new ParcelService();

    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [data, setData] = useState<TillageTask[]>([]);
    const [showDetail, setShowDetail] = useState(false);
    const [selectedTask, setSelectedTask] = useState<TillageTask | null>(null);


    const fetchTillageTasks = async () => {
        setLoading(true);
        try {
            const response = await parcelService.getTillageTasks(props.parcelId, props.cycleId);

            const processedData = response.map((task: TillageTask) => ({
                ...task,
                date: new Date(task.date), // Convert date string to Date object
            }));

            setData(processedData);
        } catch (error) {
            console.error("Error fetching tillage tasks:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTillageTasks();
    }, [props.parcelId]);

    const toggleModal = () => {
        setShowModal((prev) => !prev);
    };

    const toggleDetail = (task?: TillageTask) => {
        setSelectedTask(task || null);
        setShowDetail((prev) => !prev);
    };

    const handleDelete = (id: string) => {
        parcelService.deleteTillageTask(props.parcelId, props.cycleId, Number(id))
        setData((prevData) => prevData.filter(entry => entry.id !== id));
    }

    const updateRow = (updated: TillageTask) => {
        setData(prev => prev.map(e => e.id === updated.id ? updated : e));
        setShowDetail(false);
    };


    const truncateText = (text: string, maxWords: number, maxChars: number) => {
        if (!text) return "";

        const words = text.split(" ");
        let truncatedWords = words.slice(0, maxWords).join(" ");

        if (words.length > maxWords) {
            truncatedWords += "...";
        }

        if (truncatedWords.length > maxChars) {
            truncatedWords = truncatedWords.slice(0, maxChars) + "...";
        }

        return truncatedWords;
    };


    return (
        <div className="table-container px-1 md:px-5">
            <h1 className="text-xl font-bold pb-3 text-gray-800">Bodenbearbeitung</h1>
            {loading && (
                <div className="progress-container !h-2">
                    <div className="progress-bar"></div>
                </div>
            )}
            <div className="table-wrapper hidden md:block">
                <table>
                    <thead>
                        <tr>
                            <th>Bemerkung</th>
                            <th>Datum</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 && <tr>
                            <td colSpan={3} style={{ textAlign: "center" }}>Keine Einträge</td>
                        </tr>}
                        {data.map((row, index) => (
                            <tr key={index} onClick={() => toggleDetail(row)}>
                                <td className="note-column">{truncateText(row.note, 10, 100)}</td>
                                <td>{formatDateDisplay(row.date!)}</td>
                                <td>
                                    <span className="icon-span cursor-pointer" onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(row.id)
                                    }}>
                                        <FaRegTrashAlt />
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className='flex flex-1 flex-col w-full gap-3 md:hidden'>
                {data.map((row, index) =>
                    <div key={index} className='flex flex-1 w-full gap-5 flex-col justify-between shadow-xl border-2 rounded-lg py-4 px-3 border-gray-300'>
                        <div className='flex flex-1 flex-col gap-1'>
                            <div className='flex flex-1 gap-2 items-center'>
                                <h3 className='font-semibold text-base'>Bemerkung: </h3>
                                <p className='text-base text-gray-600'>{truncateText(row.note, 10, 50)}</p>
                            </div>
                        </div>
                        <div className='flex flex-1 justify-between'>
                            <div className='flex flex-1 gap-2 items-center'>
                                <h3 className='font-semibold text-base'>Datum: </h3>
                                <p className="text-base text-gray-600">{formatDateDisplay(row.date!)}</p>
                            </div>
                            <button className="icon-span cursor-pointer" onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(row.id)
                            }}>
                                <FaRegTrashAlt color="#ef4444" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className='flex flex-1 w-full flex-col md:hidden items-start pt-4 mb-8'>
                {data.length === 0 && <p className='text-center mb-0'>Keine Einträge</p>}
            </div>

            <IconButton className="add-btn"
                icon={<FiPlus size={26} />}
                onClick={() => toggleModal()}
            />

            <FormModal open={showModal} onClose={toggleModal} size="xl">
                <TillageCreateForm parcelId={props.parcelId} cycleId={props.cycleId} isNew={true} onSave={() => { fetchTillageTasks(); toggleModal(); }}></TillageCreateForm>
            </FormModal>
            <FormModal open={showDetail} onClose={toggleDetail} size="xl">
                <TillageCreateForm parcelId={props.parcelId} cycleId={props.cycleId} onSave={updateRow} isNew={false} task={selectedTask!} readOnly={props.cycleCompleted}></TillageCreateForm>
            </FormModal>
        </div>
    )
}

export default TillageForm;